import { useState } from 'react';
import { X, ExternalLink } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { inviaCliente, type AllegatiInvio, type RichiestaDetail } from '../../api/richieste';
import { openPdfInTab } from '../../utils/pdfPreview';

interface Props {
  richiestaId: string;
  codiceOrdine: string;
  emailDefault: string;
  onClose: () => void;
  onSaved: (updated: RichiestaDetail, warning: string | null) => void;
}

const inputCls =
  'w-full text-sm border border-stone-border rounded px-3 py-1.5 focus:outline-none focus:border-sage focus:ring-1 focus:ring-sage/20 bg-white';
const labelCls = 'block text-xs font-medium text-ink-soft mb-1';

// label/hint = chiavi i18n (tradotte al render).
const ALLEGATI_OPTIONS: { value: AllegatiInvio; label: string; hint: string }[] = [
  { value: 'entrambi', label: 'richieste.invia.opt_entrambi', hint: 'richieste.invia.opt_entrambi_hint' },
  { value: 'preventivo', label: 'richieste.invia.opt_preventivo', hint: 'richieste.invia.opt_preventivo_hint' },
  { value: 'omologa', label: 'richieste.invia.opt_omologa', hint: 'richieste.invia.opt_omologa_hint' },
  { value: 'nessuno', label: 'richieste.invia.opt_nessuno', hint: 'richieste.invia.opt_nessuno_hint' },
];

// Testo di trasmissione richiesto dal cliente (Laura, giu 2026), declinato per
// variante. Gemello del default server-side: build_corpo_trasmissione_documenti
// in backend/app/services/email_service.py — tenere allineati.
function defaultBody(allegati: AllegatiInvio, codiceOrdine: string): string {
  // corpo email (contenuto messaggi = Fase 2, fuori scope)
  const [oggetto, relativo, coda] =
    allegati === 'preventivo' ? ['il preventivo', 'relativo', 'compilato e firmato'] // i18n-ignore: email Fase 2
    : allegati === 'omologa' ? ['la scheda omologa', 'relativa', 'compilata e firmata']
    : ['il preventivo e la scheda omologa', 'relativi', 'compilati e firmati'];
  return `Buongiorno,\n\nIn allegato ${oggetto} ${relativo} all'ordine ${codiceOrdine}, da restituire ${coda}.\n\nCordiali saluti,\nEllezeta Building`;
}

function defaultSubject(allegati: AllegatiInvio, codiceOrdine: string): string {
  const oggetto =
    allegati === 'preventivo' ? 'Preventivo'
    : allegati === 'omologa' ? 'Scheda omologa'
    : 'Preventivo + scheda omologa';
  return `${oggetto} — ordine ${codiceOrdine}`;
}

/**
 * Transizione 2 (preventivo_omologa_generati → inviato_cliente, #95 + #107c).
 * Invio selettivo: scelta allegati (preventivo / omologa / entrambi / nessuno).
 * "nessuno" → registrazione senza email (consegna manuale). Oggetto/corpo si
 * adattano alla selezione finché l'utente non li modifica a mano.
 */
export default function InviaClienteModal({
  richiestaId,
  codiceOrdine,
  emailDefault,
  onClose,
  onSaved,
}: Props) {
  const { t } = useTranslation();
  const [allegati, setAllegati] = useState<AllegatiInvio>('entrambi');
  const [emailTo, setEmailTo] = useState(emailDefault);
  const [emailCc, setEmailCc] = useState('');
  const [subject, setSubject] = useState(defaultSubject('entrambi', codiceOrdine));
  const [body, setBody] = useState(defaultBody('entrambi', codiceOrdine));
  // Finché l'utente non edita, oggetto/corpo seguono la selezione allegati.
  const [subjectTouched, setSubjectTouched] = useState(false);
  const [bodyTouched, setBodyTouched] = useState(false);
  const [forceSend, setForceSend] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const manuale = allegati === 'nessuno';

  function changeAllegati(next: AllegatiInvio) {
    setAllegati(next);
    if (next !== 'nessuno') {
      if (!subjectTouched) setSubject(defaultSubject(next, codiceOrdine));
      if (!bodyTouched) setBody(defaultBody(next, codiceOrdine));
    }
  }

  async function handleAnteprima(tipo: 'preventivo' | 'scheda-omologa') {
    try {
      await openPdfInTab(`/api/richieste/${richiestaId}/${tipo}-pdf`);
    } catch {
      setError(t('richieste.invia.errore_anteprima', { tipo }));
    }
  }

  async function handleInvia() {
    setError(null);

    if (manuale) {
      setLoading(true);
      try {
        const resp = await inviaCliente(richiestaId, { allegati: 'nessuno' });
        onSaved(resp, resp.email_warning);
      } catch (err: unknown) {
        const r = (err as { response?: { data?: { detail?: string } } })?.response;
        setError(r?.data?.detail ?? t('richieste.invia.errore_manuale'));
      } finally {
        setLoading(false);
      }
      return;
    }

    if (!emailTo.trim()) {
      setError(t('richieste.invia.val_destinatario'));
      return;
    }
    if (!subject.trim() || !body.trim()) {
      setError(t('richieste.invia.val_oggetto_corpo'));
      return;
    }

    setLoading(true);
    try {
      const cc = emailCc.split(',').map((s) => s.trim()).filter(Boolean);
      const resp = await inviaCliente(richiestaId, {
        email_to: emailTo.trim(),
        email_cc: cc.length ? cc : null,
        email_subject: subject.trim(),
        email_body: body,
        force_send_failed: forceSend,
        allegati,
      });
      onSaved(resp, resp.email_warning);
    } catch (err: unknown) {
      const r = (err as { response?: { data?: { detail?: string } } })?.response;
      const detail = r?.data?.detail ?? t('richieste.invia.errore_invio');
      if (typeof detail === 'string' && /SMTP|email/i.test(detail) && !forceSend) {
        setError(t('richieste.invia.errore_invio_force', { detail }));
      } else {
        setError(typeof detail === 'string' ? detail : t('richieste.invia.errore_invio'));
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} aria-hidden="true" />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-xl mx-4 max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-stone-mid flex-shrink-0">
          <h2 className="text-base font-semibold text-ink-dark">
            {t('richieste.invia.titolo')}
          </h2>
          <button type="button" onClick={onClose} className="text-ink-soft hover:text-ink-dark">
            <X size={18} />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4 overflow-y-auto">
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => handleAnteprima('preventivo')}
              className="flex items-center justify-center gap-2 px-3 py-2 text-sm text-sage border border-sage rounded-lg hover:bg-sage-light transition-colors"
            >
              <ExternalLink size={14} /> {t('richieste.invia.anteprima_preventivo')}
            </button>
            <button
              type="button"
              onClick={() => handleAnteprima('scheda-omologa')}
              className="flex items-center justify-center gap-2 px-3 py-2 text-sm text-sage border border-sage rounded-lg hover:bg-sage-light transition-colors"
            >
              <ExternalLink size={14} /> {t('richieste.invia.anteprima_omologa')}
            </button>
          </div>

          {/* Scelta allegati (#107c) */}
          <div>
            <label className={labelCls}>{t('richieste.invia.cosa_inviare')}</label>
            <div className="grid grid-cols-2 gap-2">
              {ALLEGATI_OPTIONS.map((opt) => {
                const active = allegati === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => changeAllegati(opt.value)}
                    className={`text-left px-3 py-2 rounded-lg border transition-colors ${
                      active
                        ? 'border-sage bg-sage-light'
                        : 'border-stone-border hover:bg-stone-bg'
                    }`}
                  >
                    <p className={`text-sm font-medium ${active ? 'text-sage-dark' : 'text-ink-dark'}`}>
                      {t(opt.label)}
                    </p>
                    <p className="text-xs text-ink-soft">{t(opt.hint)}</p>
                  </button>
                );
              })}
            </div>
          </div>

          {manuale ? (
            <div className="rounded-lg border border-stone-mid bg-stone-bg/50 px-4 py-3">
              <p className="text-sm text-ink-mid">
                {t('richieste.invia.manuale_info')}
              </p>
            </div>
          ) : (
            <>
              <div>
                <label className={labelCls}>
                  {t('richieste.invia.destinatario')} <span className="text-accent-danger">*</span>
                </label>
                <input className={inputCls} type="email" value={emailTo}
                  onChange={(e) => setEmailTo(e.target.value)} />
              </div>
              <div>
                <label className={labelCls}>{t('richieste.invia.cc')}</label>
                <input className={inputCls} type="text" value={emailCc}
                  onChange={(e) => setEmailCc(e.target.value)}
                  placeholder="email1@example.com, email2@example.com" />
              </div>
              <div>
                <label className={labelCls}>
                  {t('richieste.invia.oggetto')} <span className="text-accent-danger">*</span>
                </label>
                <input className={inputCls} type="text" value={subject}
                  onChange={(e) => { setSubject(e.target.value); setSubjectTouched(true); }} />
              </div>
              <div>
                <label className={labelCls}>
                  {t('richieste.invia.corpo_email')} <span className="text-accent-danger">*</span>
                </label>
                <textarea className={inputCls} rows={6} value={body}
                  onChange={(e) => { setBody(e.target.value); setBodyTouched(true); }} />
              </div>

              <label className="flex items-center gap-2 cursor-pointer text-sm text-ink-mid">
                <input type="checkbox" checked={forceSend}
                  onChange={(e) => setForceSend(e.target.checked)}
                  className="w-4 h-4 accent-sage cursor-pointer" />
                {t('richieste.invia.force_send')}
              </label>
            </>
          )}

          {error && (
            <div className="px-3 py-2 bg-accent-danger-bg border border-[#FECACA] rounded text-sm text-accent-danger">
              {error}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-stone-mid bg-stone-bg flex-shrink-0">
          <button type="button" onClick={onClose} disabled={loading}
            className="px-4 py-2 text-sm font-medium text-ink-mid border border-stone-border rounded-lg hover:bg-white transition-colors disabled:opacity-50">
            {t('common.azioni.annulla')}
          </button>
          <button type="button" onClick={handleInvia} disabled={loading}
            className="px-4 py-2 text-sm font-medium text-white bg-sage rounded-lg hover:bg-[#5C6F59] transition-colors disabled:opacity-60">
            {loading ? t('richieste.invia.invio_in_corso') : manuale ? t('richieste.invia.btn_manuale') : t('richieste.invia.btn_invia')}
          </button>
        </div>
      </div>
    </div>
  );
}
